import React, { useState, useEffect } from "react";
import { Button, Snackbar, Alert, CircularProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AttachEmailIcon from "@mui/icons-material/AttachEmail";
import FoliumMap from "../FoliumMap";
import { authorization, fetchOrUpdateIdToken } from "../../auth/Authorization";
import { processAsyncDocument } from "../../utils/globalFunctions";
import { localConfig } from "../../config/localConfig";
import { attachSession, isValidSessionError, handleSessionExpired } from "../../utils/sessionManager";
import { useCachedContent } from "../../utils/reactCacheWrapper";
import ApplicantSectionTabs from "../Common/ApplicantSectionTabs";
import SectionsPanel from "../Common/SectionsPanel";
import HtmlContent from "../Common/HtmlContent";

const MainContent = ({
	activeTab,
	tabsData,
	finReference,
	emailID,
	loading,
	error,
	displayTypes = {},
	applicantNames = {},
	disclaimer = "",
	source,
	idToken,
	exp
}) => {
	const [activeApplicant, setActiveApplicant] = useState(null);
	const [downloading, setDownloading] = useState(false);
	const [downloadErrorOpen, setDownloadErrorOpen] = useState(false);
	const [downloadSuccessOpen, setDownloadSuccessOpen] = useState(false);
	const [emailErrorOpen, setEmailErrorOpen] = useState(false);
	const [emailSuccessOpen, setEmailSuccessOpen] = useState(false);

	// Resolve cached content (if any) for STRING-type tab content at the
	// component's top level, since React Hooks cannot be called inside
	// nested functions/callbacks (e.g. renderContent or its inline IIFE).
	const currentTabContent =
		activeTab && tabsData[activeTab] ? tabsData[activeTab] : null;
	const resolvedStringContent = useCachedContent(
		typeof currentTabContent === "string" ? currentTabContent : "",
		finReference,
		activeTab
	);

	// Function to resolve UUID to readable name
	const getApplicantDisplayName = (applicantId) => {
		// If applicantNames mapping exists and has the ID, return the name
		if (applicantNames && applicantNames[applicantId]) {
			return applicantNames[applicantId];
		}
		// Otherwise return the original ID (fallback for backward compatibility)
		return applicantId;
	};

	// Content type detection function
	const detectContentType = (content, displayType) => {
		// If displayType is explicitly provided, use it
		if (displayType) {
			return displayType.toUpperCase();
		}

		// Auto-detection fallback
		if (typeof content === "string") return "STRING";
		if (
			typeof content === "object" &&
			content !== null &&
			!Array.isArray(content)
		) {
			const keys = Object.keys(content);
			if (keys.length === 0) return "STRING";

			const firstValue = content[keys[0]];

			if (typeof firstValue === "string") {
				return "TABS"; // Document2/3 type - default to tabs
			} else if (typeof firstValue === "object" && firstValue !== null) {
				return "APPLICANT_SECTIONS"; // Document1 type
			}
		}
		return "STRING";
	};

	// Reset applicant selection when tab changes and auto-select first applicant
	useEffect(() => {
		if (activeTab && tabsData[activeTab]) {
			const content = tabsData[activeTab];
			const contentType = detectContentType(
				content,
				displayTypes[activeTab]
			);

			if (
				contentType === "APPLICANT_SECTIONS" ||
				contentType === "TABS"
			) {
				if (typeof content === "object" && content !== null) {
					const applicants = Object.keys(content);
					if (applicants.length > 0) {
						setActiveApplicant(applicants[0]); // Auto-select first applicant
					}
				}
			} else {
				setActiveApplicant(null);
			}
		} else {
			setActiveApplicant(null);
		}
	}, [activeTab, tabsData, displayTypes]);

	// Cache management: Clear stale cache entries when new data arrives
	useEffect(() => {
		if (activeTab && tabsData[activeTab]) {
			// Optional: Clear cache for tabs that have new data
			// This ensures fresh content takes precedence over cached content
			// Uncomment the line below if you want to clear cache on new API responses
			// tabStateCache.clearTab(activeTab);
		}
	}, [activeTab, tabsData]);

	// Initialize global functions for async document processing
	useEffect(() => {
		if (typeof window !== "undefined") {
			window.processAsyncDocument = processAsyncDocument;
		}
	}, []);


	const handleDownload = async () => {
		setDownloading(true);
		try {
			const app_name = localConfig.appName;
			const validatedIdToken = await fetchOrUpdateIdToken(
				idToken,
				exp
			);
			const authResult = await authorization(
				validatedIdToken.idToken,
				app_name
			);

			// Check if user has access to the application
			if (!authResult.hasAccess) {
				setDownloadErrorOpen(true);
				return;
			}

			const downloadUrl = `${localConfig.apiBaseUrl}/download`;
			const response = await fetch(downloadUrl, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: authResult.accessToken,
				},
				body: JSON.stringify(attachSession({
					user: emailID,
					query: finReference,
					source: source || "system",
				})),
			});

			if (!response.ok) {
				if (await isValidSessionError(response)) {
					handleSessionExpired();
					return;
				}
				throw new Error("Network response was not ok");
			}

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const filename = `${finReference}_PRE_PD_CAM.pdf`;
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			setDownloadSuccessOpen(true);
		} catch (error) {
			setDownloadErrorOpen(true);
		} finally {
			setDownloading(false);
		}
	};

	const handleSendEmail = async () => {
		try {
			const app_name = localConfig.appName;
			const validatedIdToken = await fetchOrUpdateIdToken(
				idToken,
				exp
			);
			const authResult = await authorization(
				validatedIdToken.idToken,
				app_name
			);

			// Check if user has access to the application
			if (!authResult.hasAccess) {
				setEmailErrorOpen(true);
				return;
			}

			const emailUrl = `${localConfig.apiBaseUrl}/mail`;
			const emailResponse = await fetch(emailUrl, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: authResult.accessToken,
				},
				body: JSON.stringify(attachSession({
					user: emailID,
					query: finReference,
					source: source || "system",
				})),
			});

			if (await isValidSessionError(emailResponse)) {
				handleSessionExpired();
				return;
			}

			setEmailSuccessOpen(true);
		} catch (error) {
			setEmailErrorOpen(true);
		}
	};

	const renderContent = () => {
		if (error) {
			return (
				<div className="content-error">
					<div className="content-error-box">
						<svg
							style={{
								width: "48px",
								height: "48px",
								margin: "0 auto 16px",
							}}
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
							/>
						</svg>
						<p style={{ fontWeight: "500", marginBottom: "8px" }}>
							Error
						</p>
						<div
							style={{ fontSize: "0.875rem" }}
							dangerouslySetInnerHTML={{ __html: error }}
						/>
					</div>
				</div>
			);
		}

		if (!activeTab || !tabsData[activeTab]) {
			return (
				<div className="content-empty">
					<div className="content-empty-box">
						<svg
							style={{
								width: "64px",
								height: "64px",
								margin: "0 auto 16px",
								color: "#d1d5db",
							}}
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
							/>
						</svg>
						<p
							style={{
								fontSize: "1.125rem",
								fontWeight: "500",
								marginBottom: "8px",
							}}
						>
							No Content Selected
						</p>
						<p style={{ fontSize: "0.875rem" }}>
							Select a document from the sidebar to view its
							content
						</p>
					</div>
				</div>
			);
		}

		const content = tabsData[activeTab];
		const contentType = detectContentType(content, displayTypes[activeTab]);

		// Enhanced content rendering based on detected type
		return (
			<div className="content-main">
				{/* Disclaimer Banner */}
				{disclaimer && (
					<div className="disclaimer-banner">
						<div className="disclaimer-content">
							<svg
								style={{
									width: "20px",
									height: "20px",
									flexShrink: 0,
								}}
								fill="currentColor"
								viewBox="0 0 24 24"
							>
								<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
							</svg>
							<span>{disclaimer}</span>
						</div>
					</div>
				)}

				{/* Action Buttons for CAM */}
				{activeTab?.toLowerCase() === "one-pager cam" && (
					<div className="content-actions">
						<div className="content-actions-row">
							<div className="content-actions-left">
								<Button
									variant="contained"
									onClick={handleDownload}
									disabled={downloading}
									startIcon={
										downloading ? (
											<CircularProgress
												size={16}
												color="inherit"
											/>
										) : (
											<DownloadIcon />
										)
									}
									style={{
										backgroundColor: "#8B5E3C",
										color: "#fff",
										"&:hover": {
											backgroundColor: "#7a5235",
										},
									}}
									size="small"
								>
									{downloading
										? "Downloading..."
										: "Download PDF"}
								</Button>
								<Button
									variant="contained"
									onClick={handleSendEmail}
									startIcon={<AttachEmailIcon />}
									style={{
										backgroundColor: "#8B5E3C",
										color: "#fff",
										"&:hover": {
											backgroundColor: "#7a5235",
										},
									}}
									size="small"
								>
									Send via Email
								</Button>
							</div>
						</div>
					</div>
				)}

				{/* Enhanced Content Rendering */}
				{contentType === "APPLICANT_SECTIONS" && (
					<>
						{/* Applicant Tabs */}
						<div className="applicant-tabs">
							<div className="applicant-tabs-row">
								{Object.keys(content).map((applicant) => (
									<button
										key={applicant}
										onClick={() =>
											setActiveApplicant(applicant)
										}
										className={`applicant-tab ${
											activeApplicant === applicant
												? "active"
												: ""
										} has-sections`}
									>
										{getApplicantDisplayName(applicant)}
									</button>
								))}
							</div>
						</div>

						{/* Applicant Content with Sections */}
						<div className="content-body">
							<ApplicantSectionTabs
								applicantData={content[activeApplicant]}
								applicantName={getApplicantDisplayName(
									activeApplicant
								)}
								leadId={finReference}
								docType={activeTab}
							/>
						</div>
					</>
				)}

				{contentType === "TABS" && (
					<>
						{/* Regular Applicant Tabs */}
						<div className="applicant-tabs">
							<div className="applicant-tabs-row">
								{Object.keys(content).map((applicant) => (
									<button
										key={applicant}
										onClick={() =>
											setActiveApplicant(applicant)
										}
										className={`applicant-tab ${
											activeApplicant === applicant
												? "active"
												: ""
										}`}
									>
										{getApplicantDisplayName(applicant)}
									</button>
								))}
							</div>
						</div>

						{/* Regular Applicant Content */}
						<div className="content-body">
							<ApplicantSectionTabs
								applicantData={content[activeApplicant]}
								applicantName={getApplicantDisplayName(
									activeApplicant
								)}
								leadId={finReference}
								docType={activeTab}
							/>
						</div>
					</>
				)}

				{contentType === "SECTIONS" && (
					<div className="content-body">
						<SectionsPanel sections={content} />
					</div>
				)}

				{contentType === "STRING" && (
					<div className="content-body">
						{(() => {
							const isMapContent =
								typeof content === "string" &&
								content.includes("<!DOCTYPE html>") &&
								(content.includes("leaflet") ||
									content.includes("L.map") ||
									content.includes("folium-map"));

							return isMapContent ? (
								<div className="content-padding">
									<FoliumMap
										htmlContent={content}
										height="600px"
										width="100%"
									/>
								</div>
							) : (
								<div className="content-padding">
									<HtmlContent
										className="prose"
										html={resolvedStringContent}
									/>
								</div>
							);
						})()}
					</div>
				)}
			</div>
		);
	};

	return (
		<div className="content-area">
			{renderContent()}

			{/* Snackbars */}
			<Snackbar
				open={downloadErrorOpen}
				autoHideDuration={5000}
				onClose={() => setDownloadErrorOpen(false)}
			>
				<Alert
					onClose={() => setDownloadErrorOpen(false)}
					severity="error"
				>
					Some error occurred while generating the PDF
				</Alert>
			</Snackbar>

			<Snackbar
				open={downloadSuccessOpen}
				autoHideDuration={5000}
				onClose={() => setDownloadSuccessOpen(false)}
			>
				<Alert
					onClose={() => setDownloadSuccessOpen(false)}
					severity="success"
				>
					PDF generated successfully
				</Alert>
			</Snackbar>

			<Snackbar
				open={emailErrorOpen}
				autoHideDuration={5000}
				onClose={() => setEmailErrorOpen(false)}
			>
				<Alert
					onClose={() => setEmailErrorOpen(false)}
					severity="error"
				>
					Some error occurred while sending the email
				</Alert>
			</Snackbar>

			<Snackbar
				open={emailSuccessOpen}
				autoHideDuration={5000}
				onClose={() => setEmailSuccessOpen(false)}
			>
				<Alert
					onClose={() => setEmailSuccessOpen(false)}
					severity="success"
				>
					Pre-PD credit memo has been sent, please check your email
					for the same
				</Alert>
			</Snackbar>
		</div>
	);
};

export default MainContent;
